import { transcribeAudio } from './api';
import { isNative, platform } from './platform';
import { nativeVoice, type NativeVoiceEvent } from './native-voice';

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<
    { isFinal: boolean; 0: { transcript: string } } & { length: number }
  >;
}

export interface SttHandlers {
  onPartial?: (text: string) => void;
  onFinal: (text: string) => void;
  onError?: (message: string) => void;
  onEnd?: () => void;
}

export interface SttSession {
  stop(): void;
  cancel(): void;
  readonly mode: 'browser' | 'server' | 'native';
}

interface ListenerHandle {
  remove(): Promise<void>;
}

export const browserSttSupported =
  typeof window !== 'undefined' &&
  Boolean(
    (window as unknown as { SpeechRecognition?: unknown }).SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: unknown })
        .webkitSpeechRecognition,
  );

function getRecognition(): SpeechRecognitionLike | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

function languageCode(language: 'ru' | 'en'): string {
  return language === 'en' ? 'en-US' : 'ru-RU';
}

async function startNativeStt(
  handlers: SttHandlers,
  language: 'ru' | 'en',
): Promise<SttSession> {
  const availability = await nativeVoice.isSttAvailable().catch(() => null);
  if (!availability?.available) throw new Error('Распознавание недоступно');

  let ended = false;
  const handles: ListenerHandle[] = [];
  const cleanup = () => {
    for (const handle of handles.splice(0)) {
      void handle.remove().catch(() => undefined);
    }
  };
  const finish = (error?: string) => {
    if (ended) return;
    ended = true;
    if (error) handlers.onError?.(error);
    handlers.onEnd?.();
    cleanup();
  };
  const add = async (
    event: 'sttPartial' | 'sttFinal' | 'sttError' | 'sttEnd',
  ) => {
    handles.push(
      await nativeVoice.addListener(event, (data: NativeVoiceEvent) => {
        if (ended) return;
        if (event === 'sttPartial') {
          if (data.text) handlers.onPartial?.(data.text);
        } else if (event === 'sttFinal') {
          const text = String(data.text ?? '').trim();
          if (text) handlers.onFinal(text);
          finish();
        } else if (event === 'sttError') {
          finish(data.message ?? data.code ?? 'Ошибка распознавания');
        } else {
          finish();
        }
      }),
    );
  };

  try {
    await add('sttPartial');
    await add('sttFinal');
    await add('sttError');
    await add('sttEnd');
    await nativeVoice.startStt({ language: languageCode(language) });
  } catch (error) {
    cleanup();
    throw error;
  }

  return {
    mode: 'native',
    stop: () => {
      void nativeVoice.stopStt().catch(() => undefined);
    },
    cancel: () => {
      if (ended) return;
      ended = true;
      void nativeVoice.cancelStt().catch(() => undefined);
      cleanup();
      handlers.onEnd?.();
    },
  };
}

function microphoneError(error: unknown): Error {
  const value = error as { name?: string };
  if (value?.name === 'NotAllowedError' || value?.name === 'SecurityError') {
    return new Error(
      isNative()
        ? 'Нет доступа к микрофону. Разрешите его в настройках приложения'
        : 'Нет доступа к микрофону. Разрешите доступ в настройках браузера',
    );
  }
  if (value?.name === 'NotFoundError') return new Error('Микрофон не найден');
  if (value?.name === 'NotReadableError') {
    return new Error('Микрофон занят другим приложением');
  }
  return error instanceof Error ? error : new Error(String(error));
}

async function serverSttSession(
  familyId: string,
  handlers: SttHandlers,
  language: 'ru' | 'en',
): Promise<SttSession> {
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (error) {
    throw microphoneError(error);
  }
  const chunks: Blob[] = [];
  const recorder = new MediaRecorder(stream);
  recorder.ondataavailable = (event) => {
    if (event.data.size) chunks.push(event.data);
  };
  const stopped = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve();
  });
  recorder.start();
  let cancelled = false;
  const cleanup = () => stream.getTracks().forEach((track) => track.stop());

  return {
    mode: 'server',
    cancel() {
      cancelled = true;
      try {
        recorder.stop();
      } catch {
        // ignore
      }
      cleanup();
      handlers.onEnd?.();
    },
    stop() {
      try {
        recorder.stop();
      } catch {
        // ignore
      }
      cleanup();
      void (async () => {
        await stopped;
        if (cancelled) return;
        try {
          const blob = new Blob(chunks, {
            type: recorder.mimeType || 'audio/webm',
          });
          const wav = await toWav16k(blob);
          const text = await transcribeAudio(familyId, wav, language);
          handlers.onFinal(text);
        } catch (error) {
          handlers.onError?.((error as Error).message);
        } finally {
          handlers.onEnd?.();
        }
      })();
    },
  };
}

async function toWav16k(blob: Blob): Promise<Blob> {
  const arrayBuffer = await blob.arrayBuffer();
  const AudioContextCtor =
    (
      window as unknown as {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      }
    ).AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext;
  const context = new AudioContextCtor();
  try {
    const decoded = await context.decodeAudioData(arrayBuffer.slice(0));
    const targetRate = 16000;
    const channels = Math.min(1, decoded.numberOfChannels);
    const length = Math.ceil(
      (decoded.length * targetRate) / decoded.sampleRate,
    );
    const offline = new OfflineAudioContext(channels, length, targetRate);
    const source = offline.createBufferSource();
    source.buffer = decoded;
    source.connect(offline.destination);
    source.start();
    const rendered = await offline.startRendering();
    return encodeWav(rendered.getChannelData(0), targetRate);
  } finally {
    void context.close();
  }
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeString = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  let offset = 44;
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index] ?? 0));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += 2;
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

export async function startStt(
  handlers: SttHandlers,
  options: {
    familyId: string;
    language: 'ru' | 'en';
    forceServer?: boolean;
  },
): Promise<SttSession> {
  if (isNative() && platform() === 'android') {
    try {
      return await startNativeStt(handlers, options.language);
    } catch (error) {
      console.warn(
        '[stt] native unavailable, falling back:',
        (error as Error).message,
      );
    }
  }

  const recognition = getRecognition();
  if (recognition && !options.forceServer && !isNative()) {
    recognition.lang = languageCode(options.language);
    recognition.continuous = false;
    recognition.interimResults = true;
    let finalText = '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (!result) continue;
        if (result.isFinal) finalText += result[0].transcript;
        else interim += result[0].transcript;
      }
      handlers.onPartial?.((finalText + interim).trim());
    };
    recognition.onerror = (event) => {
      if (event.error !== 'aborted') {
        handlers.onError?.(event.error);
      }
    };
    recognition.onend = () => {
      handlers.onFinal(finalText.trim());
      handlers.onEnd?.();
    };
    recognition.start();
    return {
      mode: 'browser',
      stop: () => recognition.stop(),
      cancel: () => recognition.abort(),
    };
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Микрофон недоступен');
  }
  return serverSttSession(options.familyId, handlers, options.language);
}
